import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';
import ApiService from '../services/apiService';

interface Quote {
  quoteId: string;
  type: string;
  status: string;
  premium?: {
    monthly: number;
    annually: number;
    currency: string;
  };
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
  };
  validUntil: string;
  createdAt: string;
  updatedAt: string;
}

const QuotesHistory: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuotes();
    }
  }, [isAuthenticated]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getUserQuotes();

      if (response.success && response.data) {
        setQuotes(response.data);
      } else {
        setError(response.error || 'Failed to fetch quotes');
      }
    } catch (err) {
      setError('Failed to fetch quotes');
      console.error('Error fetching quotes:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return '#28a745';
      case 'pending': return '#ffc107';
      case 'declined': return '#dc3545';
      case 'expired': return '#6c757d';
      default: return '#007bff';
    }
  };

  const getTypeDisplay = (type: string) => {
    switch (type) {
      case 'auto': return 'Auto Insurance';
      case 'home': return 'Home Insurance';
      case 'life': return 'Life Insurance';
      case 'health': return 'Health Insurance';
      default: return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (validUntil: string) => {
    return new Date(validUntil) < new Date();
  };

  return (
    <ProtectedRoute>
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: '2rem 0'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2.5rem' }}>
                My Insurance Quotes
              </h1>
              <p style={{ margin: 0, opacity: 0.9 }}>
                View and manage your insurance quote history
              </p>
            </div>

            {/* Content */}
            <div style={{ padding: '2rem' }}>
              {loading ? (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem',
                  color: '#666'
                }}>
                  <div style={{
                    fontSize: '1.2rem',
                    marginBottom: '1rem'
                  }}>
                    Loading your quotes...
                  </div>
                </div>
              ) : error ? (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem'
                }}>
                  <div style={{
                    backgroundColor: '#fee',
                    color: '#c33',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    {error}
                  </div>
                  <button
                    onClick={fetchQuotes}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Try Again
                  </button>
                </div>
              ) : quotes.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem',
                  color: '#666'
                }}>
                  <h3>No quotes found</h3>
                  <p>You haven't requested any insurance quotes yet.</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gap: '1.5rem'
                }}>
                  {quotes.map((quote) => (
                    <div key={quote.quoteId} style={{
                      border: '1px solid #e9ecef',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      backgroundColor: '#fff',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '1rem',
                        alignItems: 'start'
                      }}>
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            marginBottom: '1rem'
                          }}>
                            <h3 style={{
                              margin: 0,
                              color: '#333',
                              fontSize: '1.25rem'
                            }}>
                              {getTypeDisplay(quote.type)}
                            </h3>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: 'white',
                              backgroundColor: getStatusColor(quote.status),
                              textTransform: 'capitalize'
                            }}>
                              {quote.status}
                            </span>
                            {isExpired(quote.validUntil) && (
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: '20px',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: 'white',
                                backgroundColor: '#dc3545'
                              }}>
                                Expired
                              </span>
                            )}
                          </div>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '1rem',
                            marginBottom: '1rem'
                          }}>
                            <div>
                              <label style={{
                                fontSize: '0.875rem',
                                color: '#666',
                                fontWeight: '600'
                              }}>Quote ID</label>
                              <div style={{ color: '#333' }}>
                                {quote.quoteId.substring(0, 8)}...
                              </div>
                            </div>
                            <div>
                              <label style={{
                                fontSize: '0.875rem',
                                color: '#666',
                                fontWeight: '600'
                              }}>Created</label>
                              <div style={{ color: '#333' }}>
                                {formatDate(quote.createdAt)}
                              </div>
                            </div>
                            <div>
                              <label style={{
                                fontSize: '0.875rem',
                                color: '#666',
                                fontWeight: '600'
                              }}>Valid Until</label>
                              <div style={{
                                color: isExpired(quote.validUntil) ? '#dc3545' : '#333'
                              }}>
                                {formatDate(quote.validUntil)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {quote.premium && (
                          <div style={{
                            textAlign: 'right',
                            padding: '1rem',
                            backgroundColor: '#f8f9ff',
                            borderRadius: '8px',
                            border: '1px solid #e2e8ff'
                          }}>
                            <div style={{
                              fontSize: '0.875rem',
                              color: '#666',
                              marginBottom: '0.25rem'
                            }}>
                              Premium
                            </div>
                            <div style={{
                              fontSize: '1.5rem',
                              fontWeight: 'bold',
                              color: '#667eea',
                              marginBottom: '0.25rem'
                            }}>
                              ${quote.premium.monthly}/mo
                            </div>
                            <div style={{
                              fontSize: '0.875rem',
                              color: '#666'
                            }}>
                              ${quote.premium.annually}/year
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default QuotesHistory;